import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import AdminLayout from '../components/Layout/AdminLayout';
import Button from '../components/UI/Button';
import Modal from '../components/UI/Modal';
import BookingCalendar from '../components/BookingCalendar';
import { bookingsAPI, hallsAPI, usersAPI } from '../services/api';
import { Eye, Calendar, Clock, User, CheckCircle, XCircle, List, CalendarDays, Plus } from 'lucide-react';
import { getHijriDate, getGregorianDate } from '../utils/dateUtils';

const Bookings = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');
  const [viewMode, setViewMode] = useState('calendar');
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [halls, setHalls] = useState([]);
  const [users, setUsers] = useState([]);

  const { register, handleSubmit, reset, formState: { errors } } = useForm();

  useEffect(() => {
    fetchBookings();
    fetchHalls();
    fetchUsers();
  }, []);

  const fetchBookings = async () => {
    try {
      const response = await bookingsAPI.getAll();
      setBookings(response.data);
    } catch (error) {
      toast.error('فشل في تحميل الحجوزات');
    } finally {
      setLoading(false);
    }
  };

  const fetchHalls = async () => {
    try {
      const response = await hallsAPI.getAll();
      setHalls(response.data);
    } catch (error) {
      console.error('Failed to fetch halls:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await usersAPI.getAll();
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  };

  const createBooking = async (data) => {
    try {
      const payload = {
        hallId: parseInt(data.hallId),
        startTime: new Date(data.startTime).toISOString(),
        endTime: new Date(data.endTime).toISOString(),
        purpose: data.purpose,
      };

      // Add optional fields only if they have values
      if (data.notes && data.notes.trim()) {
        payload.notes = data.notes.trim();
      }

      if (data.contactPhone && data.contactPhone.trim()) {
        payload.contactPhone = data.contactPhone.trim();
      }

      await bookingsAPI.create(payload);
      toast.success('تم إنشاء الحجز بنجاح');
      setCreateModalOpen(false);
      reset();
      fetchBookings();
    } catch (error) {
      console.error('Error creating booking:', error);
      const errorMsg = error.response?.data?.message || 'فشل في إنشاء الحجز';
      toast.error(errorMsg);
    }
  };

  const updateBookingStatus = async (bookingId, status) => {
    try {
      await bookingsAPI.update(bookingId, { status });
      toast.success('تم تحديث حالة الحجز');
      fetchBookings();
      setViewModalOpen(false);
    } catch (error) {
      toast.error('فشل في تحديث حالة الحجز');
    }
  };

  const viewBookingDetails = (booking) => {
    setSelectedBooking(booking);
    setViewModalOpen(true);
  };

  const filteredBookings = bookings.filter(booking => {
    if (filterStatus === 'all') return true;
    return booking.status === filterStatus;
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'confirmed':
        return 'bg-green-100 text-green-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'pending':
        return 'في الانتظار';
      case 'confirmed':
        return 'مؤكد';
      case 'cancelled':
        return 'ملغي';
      default:
        return status;
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent-gold"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-text-primary">إدارة الحجوزات</h1>
            <p className="text-text-secondary">عرض وإدارة طلبات حجز القاعات</p>
          </div>

          <div className="flex items-center gap-3">
            <Button onClick={() => setCreateModalOpen(true)}>
              <Plus className="w-4 h-4 ml-1" />
              إنشاء حجز جديد
            </Button>
            <div className="flex items-center bg-primary-light rounded-lg p-1">
              <button
                onClick={() => setViewMode('calendar')}
                className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-colors ${
                  viewMode === 'calendar'
                    ? 'bg-accent-gold text-white'
                    : 'text-text-secondary hover:text-text-primary'
                }`}
              >
                <CalendarDays className="w-4 h-4" />
                التقويم
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-colors ${
                  viewMode === 'list'
                    ? 'bg-accent-gold text-white'
                    : 'text-text-secondary hover:text-text-primary'
                }`}
              >
                <List className="w-4 h-4" />
                القائمة
              </button>
            </div>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-3 py-2 bg-card border border-divider rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-accent-gold"
            >
              <option value="all">جميع الحجوزات</option>
              <option value="pending">في الانتظار</option>
              <option value="confirmed">مؤكدة</option>
              <option value="cancelled">ملغية</option>
            </select>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-card rounded-xl p-6 border border-divider">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-text-secondary text-sm font-medium">في الانتظار</p>
                <p className="text-2xl font-bold text-yellow-600 mt-2">
                  {bookings.filter(b => b.status === 'pending').length}
                </p>
              </div>
              <div className="bg-yellow-100 p-3 rounded-lg">
                <Clock className="h-6 w-6 text-yellow-600" />
              </div>
            </div>
          </div>

          <div className="bg-card rounded-xl p-6 border border-divider">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-text-secondary text-sm font-medium">مؤكدة</p>
                <p className="text-2xl font-bold text-green-600 mt-2">
                  {bookings.filter(b => b.status === 'confirmed').length}
                </p>
              </div>
              <div className="bg-green-100 p-3 rounded-lg">
                <CheckCircle className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </div>

          <div className="bg-card rounded-xl p-6 border border-divider">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-text-secondary text-sm font-medium">ملغية</p>
                <p className="text-2xl font-bold text-red-600 mt-2">
                  {bookings.filter(b => b.status === 'cancelled').length}
                </p>
              </div>
              <div className="bg-red-100 p-3 rounded-lg">
                <XCircle className="h-6 w-6 text-red-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Calendar View */}
        {viewMode === 'calendar' && (
          <BookingCalendar
            bookings={filteredBookings}
            onDateSelect={setSelectedDate}
            selectedDate={selectedDate}
          />
        )}

        {/* Selected Date Bookings */}
        {viewMode === 'calendar' && (
          <div className="bg-card rounded-xl border border-divider p-6">
            <h3 className="text-lg font-semibold text-text-primary mb-2">
              حجوزات اليوم
            </h3>
            <div className="text-sm text-text-muted mb-4 space-y-1">
              <p>هجري: {getHijriDate(selectedDate)}</p>
              <p>ميلادي: {getGregorianDate(selectedDate)}</p>
            </div>
            <div className="space-y-3">
              {filteredBookings
                .filter(booking => {
                  const bookingDate = new Date(booking.startTime);
                  return (
                    bookingDate.getDate() === selectedDate.getDate() &&
                    bookingDate.getMonth() === selectedDate.getMonth() &&
                    bookingDate.getFullYear() === selectedDate.getFullYear()
                  );
                })
                .map(booking => (
                  <div
                    key={booking.id}
                    className="flex items-center justify-between p-4 bg-primary-light rounded-lg hover:border-accent-gold border border-transparent transition-colors cursor-pointer"
                    onClick={() => viewBookingDetails(booking)}
                  >
                    <div className="flex items-center gap-4">
                      <div className="bg-card p-3 rounded-lg">
                        <Clock className="w-5 h-5 text-accent-gold" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-text-primary">{booking.hall?.name}</h4>
                        <p className="text-sm text-text-secondary">{booking.purpose}</p>
                        <p className="text-xs text-text-muted mt-1">
                          {new Date(booking.startTime).toLocaleTimeString('ar-SA', {
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                          {' - '}
                          {new Date(booking.endTime).toLocaleTimeString('ar-SA', {
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${getStatusColor(booking.status)}`}>
                        {getStatusText(booking.status)}
                      </span>
                      <Button variant="ghost" size="sm">
                        <Eye className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              {filteredBookings.filter(booking => {
                const bookingDate = new Date(booking.startTime);
                return (
                  bookingDate.getDate() === selectedDate.getDate() &&
                  bookingDate.getMonth() === selectedDate.getMonth() &&
                  bookingDate.getFullYear() === selectedDate.getFullYear()
                );
              }).length === 0 && (
                <div className="text-center py-8">
                  <Calendar className="mx-auto h-12 w-12 text-text-muted mb-3" />
                  <p className="text-text-muted">لا توجد حجوزات في هذا التاريخ</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Bookings Table */}
        {viewMode === 'list' && (
        <div className="bg-card rounded-xl border border-divider overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-primary-light">
                <tr>
                  <th className="px-6 py-3 text-right text-xs font-medium text-text-secondary uppercase tracking-wider">
                    المستخدم
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-text-secondary uppercase tracking-wider">
                    القاعة
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-text-secondary uppercase tracking-wider">
                    التاريخ والوقت
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-text-secondary uppercase tracking-wider">
                    الغرض
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-text-secondary uppercase tracking-wider">
                    الحالة
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-text-secondary uppercase tracking-wider">
                    التكلفة
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-text-secondary uppercase tracking-wider">
                    إجراءات
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-divider">
                {filteredBookings.map((booking) => (
                  <tr key={booking.id} className="hover:bg-primary-light">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-text-primary">
                          {booking.user?.name || 'مستخدم غير محدد'}
                        </div>
                        <div className="text-sm text-text-muted">
                          {booking.user?.email || ''}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-text-primary">
                        {booking.hall?.name || 'قاعة غير محددة'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-xs text-text-muted">
                        {getHijriDate(booking.startTime)}
                      </div>
                      <div className="text-xs text-text-muted">
                        {getGregorianDate(booking.startTime)}
                      </div>
                      <div className="text-sm text-text-muted mt-1">
                        {new Date(booking.startTime).toLocaleTimeString('ar-SA', {
                          hour: '2-digit',
                          minute: '2-digit'
                        })} - {new Date(booking.endTime).toLocaleTimeString('ar-SA', {
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-text-primary max-w-32 truncate">
                        {booking.purpose}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(booking.status)}`}>
                        {getStatusText(booking.status)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-text-primary">
                      {booking.totalCost} جنيه مصري
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => viewBookingDetails(booking)}
                      >
                        <Eye className="w-4 h-4 ml-1" />
                        عرض
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {filteredBookings.length === 0 && (
            <div className="text-center py-12">
              <Calendar className="mx-auto h-12 w-12 text-text-muted mb-4" />
              <h3 className="text-lg font-medium text-text-primary mb-2">لا توجد حجوزات</h3>
              <p className="text-text-muted">
                {filterStatus === 'all' 
                  ? 'لا توجد حجوزات في النظام' 
                  : `لا توجد حجوزات ${getStatusText(filterStatus)}`
                }
              </p>
            </div>
          )}
        </div>
        )}
      </div>

      {/* Booking Details Modal */}
      <Modal
        isOpen={viewModalOpen}
        onClose={() => setViewModalOpen(false)}
        title="تفاصيل الحجز"
        size="lg"
      >
        {selectedBooking && (
          <div className="space-y-6">
            {/* Status */}
            <div className="flex items-center justify-between">
              <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${getStatusColor(selectedBooking.status)}`}>
                {getStatusText(selectedBooking.status)}
              </span>
              <div className="text-sm text-text-muted">
                رقم الحجز: #{selectedBooking.id}
              </div>
            </div>

            {/* User Info */}
            <div className="bg-primary-light rounded-lg p-4">
              <h4 className="text-lg font-medium text-text-primary mb-3 flex items-center">
                <User className="w-5 h-5 ml-2" />
                معلومات المستخدم
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-text-muted">الاسم</p>
                  <p className="text-text-primary">{selectedBooking.user?.name || 'غير محدد'}</p>
                </div>
                <div>
                  <p className="text-sm text-text-muted">البريد الإلكتروني</p>
                  <p className="text-text-primary">{selectedBooking.user?.email || 'غير محدد'}</p>
                </div>
                <div>
                  <p className="text-sm text-text-muted">رقم الهاتف</p>
                  <p className="text-text-primary">{selectedBooking.contactPhone || selectedBooking.user?.phone || 'غير محدد'}</p>
                </div>
              </div>
            </div>

            {/* Booking Details */}
            <div className="bg-primary-light rounded-lg p-4">
              <h4 className="text-lg font-medium text-text-primary mb-3 flex items-center">
                <Calendar className="w-5 h-5 ml-2" />
                تفاصيل الحجز
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-text-muted">القاعة</p>
                  <p className="text-text-primary">{selectedBooking.hall?.name || 'غير محددة'}</p>
                </div>
                <div>
                  <p className="text-sm text-text-muted">الغرض</p>
                  <p className="text-text-primary">{selectedBooking.purpose}</p>
                </div>
                <div>
                  <p className="text-sm text-text-muted">تاريخ البداية</p>
                  <div className="text-text-primary space-y-1">
                    <p className="text-xs">هجري: {getHijriDate(selectedBooking.startTime)}</p>
                    <p className="text-xs">ميلادي: {getGregorianDate(selectedBooking.startTime)}</p>
                    <p className="text-sm">
                      {new Date(selectedBooking.startTime).toLocaleTimeString('ar-SA', {
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                  </div>
                </div>
                <div>
                  <p className="text-sm text-text-muted">تاريخ النهاية</p>
                  <div className="text-text-primary space-y-1">
                    <p className="text-xs">هجري: {getHijriDate(selectedBooking.endTime)}</p>
                    <p className="text-xs">ميلادي: {getGregorianDate(selectedBooking.endTime)}</p>
                    <p className="text-sm">
                      {new Date(selectedBooking.endTime).toLocaleTimeString('ar-SA', {
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                  </div>
                </div>
                <div>
                  <p className="text-sm text-text-muted">التكلفة الإجمالية</p>
                  <p className="text-text-primary font-bold text-lg">{selectedBooking.totalCost} جنيه مصري</p>
                </div>
                <div>
                  <p className="text-sm text-text-muted">تاريخ الحجز</p>
                  <div className="text-text-primary space-y-1">
                    <p className="text-xs">هجري: {getHijriDate(selectedBooking.createdAt)}</p>
                    <p className="text-xs">ميلادي: {getGregorianDate(selectedBooking.createdAt)}</p>
                  </div>
                </div>
              </div>
              
              {selectedBooking.notes && (
                <div className="mt-4">
                  <p className="text-sm text-text-muted mb-1">ملاحظات</p>
                  <p className="text-text-primary bg-card rounded p-3">{selectedBooking.notes}</p>
                </div>
              )}
            </div>

            {/* Actions */}
            {selectedBooking.status === 'pending' && (
              <div className="flex space-x-3 space-x-reverse justify-end pt-4 border-t border-divider">
                <Button
                  variant="danger"
                  onClick={() => updateBookingStatus(selectedBooking.id, 'cancelled')}
                >
                  <XCircle className="w-4 h-4 ml-1" />
                  رفض الحجز
                </Button>
                <Button
                  onClick={() => updateBookingStatus(selectedBooking.id, 'confirmed')}
                >
                  <CheckCircle className="w-4 h-4 ml-1" />
                  تأكيد الحجز
                </Button>
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* Create Booking Modal */}
      <Modal
        isOpen={createModalOpen}
        onClose={() => {
          setCreateModalOpen(false);
          reset();
        }}
        title="إنشاء حجز جديد"
        size="lg"
      >
        <form onSubmit={handleSubmit(createBooking)} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                المستخدم *
              </label>
              <select
                {...register('userId', { required: 'المستخدم مطلوب' })}
                className="w-full px-3 py-2 bg-card border border-divider rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-accent-gold"
              >
                <option value="">اختر المستخدم</option>
                {users.map(user => (
                  <option key={user.id} value={user.id}>{user.name} - {user.email}</option>
                ))}
              </select>
              {errors.userId && (
                <p className="text-red-500 text-sm mt-1">{errors.userId.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                القاعة *
              </label>
              <select
                {...register('hallId', { required: 'القاعة مطلوبة' })}
                className="w-full px-3 py-2 bg-card border border-divider rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-accent-gold"
              >
                <option value="">اختر القاعة</option>
                {halls.map(hall => (
                  <option key={hall.id} value={hall.id}>{hall.name}</option>
                ))}
              </select>
              {errors.hallId && (
                <p className="text-red-500 text-sm mt-1">{errors.hallId.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                تاريخ ووقت البداية *
              </label>
              <input
                type="datetime-local"
                {...register('startTime', { required: 'تاريخ البداية مطلوب' })}
                className="w-full px-3 py-2 bg-card border border-divider rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-accent-gold"
              />
              {errors.startTime && (
                <p className="text-red-500 text-sm mt-1">{errors.startTime.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                تاريخ ووقت النهاية *
              </label>
              <input
                type="datetime-local"
                {...register('endTime', { required: 'تاريخ النهاية مطلوب' })}
                className="w-full px-3 py-2 bg-card border border-divider rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-accent-gold"
              />
              {errors.endTime && (
                <p className="text-red-500 text-sm mt-1">{errors.endTime.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                رقم الهاتف *
              </label>
              <input
                type="tel"
                {...register('contactPhone', { required: 'رقم الهاتف مطلوب' })}
                className="w-full px-3 py-2 bg-card border border-divider rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-accent-gold"
                placeholder="05xxxxxxxx"
              />
              {errors.contactPhone && (
                <p className="text-red-500 text-sm mt-1">{errors.contactPhone.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                الغرض *
              </label>
              <input
                type="text"
                {...register('purpose', { required: 'الغرض مطلوب' })}
                className="w-full px-3 py-2 bg-card border border-divider rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-accent-gold"
                placeholder="مثال: حفل زفاف، محاضرة، اجتماع"
              />
              {errors.purpose && (
                <p className="text-red-500 text-sm mt-1">{errors.purpose.message}</p>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              ملاحظات إضافية
            </label>
            <textarea
              {...register('notes')}
              rows={3}
              className="w-full px-3 py-2 bg-card border border-divider rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-accent-gold"
              placeholder="أي ملاحظات إضافية عن الحجز"
            />
          </div>

          <div className="flex justify-end space-x-3 space-x-reverse pt-4 border-t border-divider">
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                setCreateModalOpen(false);
                reset();
              }}
            >
              إلغاء
            </Button>
            <Button type="submit">
              <Plus className="w-4 h-4 ml-1" />
              إنشاء الحجز
            </Button>
          </div>
        </form>
      </Modal>
    </AdminLayout>
  );
};

export default Bookings;